import { Module } from 'vuex'

interface BusinessDetail {
  installments: string | number        // 期数
  customer_rating: string              // 客户评级
  product_rate: string | number        // 产品利率
  vehicle_type: string                 // 车辆类型
  cooperation_team: string            // 合作渠道
  transfer_amount: string | number     // 转款金额
  transfer_date: string               // 转款日期
}

interface WebSocketState {
  chartRef: any | null,
  centerRef: any | null,
  centerLeft1Ref: any | null,
  centerRight2Ref: any | null,
  currentCity: string | null,  // 当前选中的城市名称
  currentCode: string | null,   // 当前选中的城市 code
  businessDetailsData: Record<string, Record<string, BusinessDetail[]>> | null,  // 业务详细数据
  scrollBoardData: any[] | null   // 右侧滚动表数据源
}

interface QiankunProps {
  websocket?: {
    addMessageHandler?: (handler: (event: MessageEvent) => void) => () => void
  }
  isAdmin?: boolean
}

declare global {
  interface Window {
    __POWERED_BY_QIANKUN__?: boolean
    __INJECTED_PROPS__?: QiankunProps
  }
}

const websocketModule: Module<WebSocketState, any> = {
  namespaced: true,
  
  state: {
    chartRef: null,
    centerRef: null,
    centerLeft1Ref: null,
    centerRight2Ref: null,
    currentCity: null,
    currentCode: null,
    businessDetailsData: null,
    scrollBoardData: null
  },

  mutations: {
    SET_CHART_REF(state, ref) {
      state.chartRef = ref
    },
    SET_CENTER_REF(state, ref) {
      state.centerRef = ref
    },
    SET_CENTER_LEFT1_REF(state, ref) {
      state.centerLeft1Ref = ref
    },
    SET_CENTER_RIGHT2_REF(state, ref) {
      state.centerRight2Ref = ref
    },
    SET_CURRENT_CITY(state, { city, code }) {
      state.currentCity = city
      state.currentCode = code
    },
    SET_BUSINESS_DETAILS_DATA(state, data) {
      state.businessDetailsData = data
    },
    SET_SCROLL_BOARD_DATA(state, data) {
      state.scrollBoardData = data
    }
  },

  actions: {
    setChartRef({ commit }, ref) {
      commit('SET_CHART_REF', ref)
    },

    setCenterRef({ commit }, ref) {
      commit('SET_CENTER_REF', ref)
    },

    setCenterLeft1Ref({ commit }, ref) {
      commit('SET_CENTER_LEFT1_REF', ref)
    },

    setCenterRight2Ref({ commit }, ref) {
      commit('SET_CENTER_RIGHT2_REF', ref)
    },

    // 设置当前选中的城市和 code
    setCurrentCity({ commit }, { city, code }) {
      commit('SET_CURRENT_CITY', { city, code })
    },
    
    // 设置业务详细数据
    setBusinessDetailsData({ commit }, data) {
      commit('SET_BUSINESS_DETAILS_DATA', data)
    },

    // 设置右侧滚动表数据
    setScrollBoardData({ commit }, data) {
      commit('SET_SCROLL_BOARD_DATA', data)
    },

    // 初始化websocket监听
    initWebsocket({ dispatch }) {
      if (window.__POWERED_BY_QIANKUN__) {
        const props = window.__INJECTED_PROPS__
        if (props?.websocket?.addMessageHandler) {
          return props.websocket.addMessageHandler((event) => {
            try {
              const data = JSON.parse(event.data)
              dispatch('handleMessage', data)
            } catch (error) {
              console.error('处理websocket消息失败:', error)
            }
          })
        }
      }
      return null
    },

    /** 统一交易事件只交给超管收益大屏；旧业务通知继续走原组件链路。 */
    handleMessage({ state }, message) {
      if (message.contentType === 'REVENUE_TRANSACTION' || message.contentType === 'REVENUE_REFRESH') {
        state.centerRef?.handleMessage?.(message)
        return
      }
      // 服务端每次连接成功都会发送这两类上线提示；重连后立即补齐漏收窗口。
      if (message.contentType === 'SYSTEM' && message.sender === 'system'
        && ['您已上线', '请查看您的未读消息~'].includes(message.content)) {
        state.centerRef?.handleReconnect?.()
        return
      }
      if (message.dump_data && message.dump_data.type) {
        const { money } = message.dump_data
        const user = message.dump_data.data?.user
        
        // 更新图表组件
        if (state.chartRef?.updateUserBalance) {
          state.chartRef.updateUserBalance(user, money)
        }

        // 更新中心组件
        if (state.centerRef?.handleMessage) {
          state.centerRef.handleMessage(message)
        }

        // 更新左侧业务组件
        if (state.centerLeft1Ref?.handleMessage) {
          state.centerLeft1Ref.handleMessage(message)
        }

        // 更新右侧业务组件
        if (state.centerRight2Ref?.handleMessage) {
          state.centerRight2Ref.handleMessage(message)
        }
      }
    },

    // 刷新所有组件数据
    async refreshAllComponents({ state }) {
      const refreshPromises = []
      
      // 刷新中心组件
      if (state.centerRef?.refreshData) {
        refreshPromises.push(state.centerRef.refreshData())
      }

      // 刷新左侧业务组件
      if (state.centerLeft1Ref?.refreshData) {
        refreshPromises.push(state.centerLeft1Ref.refreshData())
      }

      // 刷新右侧业务组件
      if (state.centerRight2Ref?.refreshData) {
        refreshPromises.push(state.centerRight2Ref.refreshData())
      }

      // 刷新图表组件
      if (state.chartRef?.refreshData) {
        refreshPromises.push(state.chartRef.refreshData())
      }

      // 并行执行所有刷新任务
      try {
        await Promise.all(refreshPromises)
      } catch (error) {
        console.error('刷新组件数据时发生错误:', error)
      }
    }
  }
}

export default websocketModule
